"use client";

import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../../lib/api";
import { queryKeys } from "../../lib/query-keys";
import { scheduleIdle } from "../../lib/idle";
import { LuCirclePlus, LuChevronRight, LuRefreshCw } from "react-icons/lu";
import { HeroBanner } from "./HeroBanner";
import { RecommendedEvents } from "./RecommendedEvents";
import { PopularEvents } from "./PopularEvents";
import { LandingNav } from "./LandingNav";
import type { Category } from "./CategoriesSection";

// Below-fold components — lazy-loaded to reduce initial JS bundle
const CategoriesSection = dynamic(() => import("./CategoriesSection").then(m => ({ default: m.CategoriesSection })));
const WhatElseSection = dynamic(() => import("./WhatElseSection").then(m => ({ default: m.WhatElseSection })));
const HostCTA = dynamic(() => import("./HostCTA").then(m => ({ default: m.HostCTA })));
const FooterSection = dynamic(() => import("./FooterSection").then(m => ({ default: m.FooterSection })));

type HostSummary = {
    id: string;
};

export type LandingInitialData = {
    featured?: unknown[];
    events?: unknown[];
    trending?: unknown[];
};

type SearchParamsState = {
    city: string;
    cityId: string;
    date: string;
    search: string;
    category: string;
    tag: string;
    minPrice: string;
    maxPrice: string;
};

const defaultParams: SearchParamsState = {
    city: "",
    cityId: "",
    date: "",
    search: "",
    category: "",
    tag: "",
    minPrice: "",
    maxPrice: "",
};

/**
 * Reads URL search params inside its own Suspense boundary.
 * During static generation / ISR this falls back to null,
 * keeping the rest of the page SSR-renderable.
 */
function SearchParamsSync({
    onChange,
}: {
    onChange: (p: SearchParamsState) => void;
}) {
    const sp = useSearchParams();
    useEffect(() => {
        onChange({
            city: sp?.get("city") ?? "",
            cityId: sp?.get("cityId") ?? "",
            date: sp?.get("date") ?? "",
            search: sp?.get("search") ?? "",
            category: sp?.get("category") ?? "",
            tag: sp?.get("tag") ?? "",
            minPrice: sp?.get("minPrice") ?? "",
            maxPrice: sp?.get("maxPrice") ?? "",
        });
    }, [sp, onChange]);
    return null;
}

interface LandingPageProps {
    initialData?: LandingInitialData;
}

export function LandingPage({ initialData }: LandingPageProps) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const queryClient = useQueryClient();
    const router = useRouter();
    const [params, setParams] = useState<SearchParamsState>(defaultParams);
    const handleParamsChange = useCallback((p: SearchParamsState) => setParams(p), []);

    const cityParam = params.city;
    const cityIdParam = params.cityId;
    const dateParam = params.date;
    const searchParam = params.search;
    const categoryParam = params.category;
    const tagParam = params.tag;
    const minPriceParam = params.minPrice;
    const maxPriceParam = params.maxPrice;
    const cityLabel = cityParam ? cityParam : "All cities";
    const hasFilters = Boolean(dateParam || searchParam || categoryParam || tagParam || minPriceParam || maxPriceParam);
    const activeCategory: Category = useMemo(
        () =>
            categoryParam
                ? { id: categoryParam, name: categoryParam }
                : { id: "all", name: "All" },
        [categoryParam]
    );

    useEffect(() => {
        return scheduleIdle(() => {
            // Prefetch events and hosts lists in parallel
            // The /hosts endpoint already returns all needed data (id, name, imageUrl)
            // so no individual host fetches are required - eliminates N+1 pattern
            void Promise.all([
                queryClient.prefetchQuery({
                    queryKey: queryKeys.events.list({ search: "", city: "", category: "" }),
                    queryFn: () => apiFetch<unknown>("/events?upcoming=true"),
                    staleTime: 60_000,
                }),
                queryClient.prefetchQuery({
                    queryKey: queryKeys.hosts.all,
                    queryFn: () => apiFetch<HostSummary[]>("/hosts"),
                    staleTime: 5 * 60_000,
                }),
            ]);
        }, 2000);
    }, [queryClient]);

    return (
        <div
            ref={containerRef}
            className="relative min-h-screen overflow-x-hidden bg-black"
            // style={{ contain: 'layout style' }}
        >
            {/* Sync URL search params without blocking SSR */}
            <Suspense fallback={null}>
                <SearchParamsSync onChange={handleParamsChange} />
            </Suspense>

            {/* U-shaped gradient fixed to viewport — single element with multi-layer background */}
            <div
                className="pointer-events-none fixed inset-0 z-[1]"
                style={{
                    contain: 'strict',
                    background: `
                        radial-gradient(ellipse 70% 6% at 50% 100%, rgba(23,21,104,0.6), transparent 100%),
                        radial-gradient(ellipse 10% 35% at 0% 100%, rgba(23,21,104,0.6), transparent 100%),
                        radial-gradient(ellipse 10% 35% at 100% 100%, rgba(23,21,104,0.6), transparent 100%)
                    `,
                }}
            >
                {/* Noise texture */}
                <div
                    className="absolute inset-0 opacity-[0.03]"
                    style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
                    }}
                />
            </div>
            {/* Static blobs — paint-contained to avoid blocking LCP paint */}
            <div
                className="pointer-events-none absolute -left-32 top-24 h-72 w-72 rounded-full opacity-25 blur-3xl"
                style={{
                    contain: 'paint',
                    background:
                        "radial-gradient(circle at 35% 35%, rgba(23,21,104,0.5), transparent 60%)",
                }}
            />
            <div
                className="pointer-events-none absolute -right-28 top-[420px] h-80 w-80 rounded-full opacity-20 blur-3xl"
                style={{
                    contain: 'paint',
                    background:
                        "radial-gradient(circle at 60% 40%, rgba(23,21,104,0.45), transparent 65%)",
                }}
            />
            <div>
                {/* Navigation — isolated Suspense so useSearchParams doesn't block SSR */}
                <Suspense fallback={null}>
                    <LandingNav />
                </Suspense>

                {/* Main content */}
                <main className="relative z-10 pb-20 md:pb-8">
                {hasFilters ? (
                    <div className="mb-2 mt-4 flex items-center justify-center">
                        <Link
                            href="/"
                            className="inline-flex items-center gap-2 rounded-full border border-[var(--hopp-panel-strong)] bg-[var(--hopp-panel)] px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-[var(--hopp-muted)] transition hover:border-[var(--hopp-sapphire)] hover:text-[var(--hopp-text)]"
                        >
                            <LuRefreshCw className="h-4 w-4 text-[var(--hopp-sapphire)]" />
                            Refresh filters
                        </Link>
                    </div>
                ) : null}

                {/* Hero Banner */}
                <div>
                    <HeroBanner initialData={initialData?.featured} />
                </div>

                {/* Recommended Events - horizontal scroll */}
                <div>
                    <RecommendedEvents initialData={initialData?.events} />
                </div>

                {/* Categories - horizontal scroll pills */}
                <div>
                    <CategoriesSection onCategorySelect={(category) => {
                        const sp = new URLSearchParams(typeof window !== "undefined" ? window.location.search : "");
                        if (category.id === "all") {
                            sp.delete("category");
                        } else {
                            sp.set("category", category.id);
                        }
                        const qs = sp.toString();
                        router.push(qs ? `/?${qs}` : "/", { scroll: false });
                    }} />
                </div>

                {/* Popular Events in City */}
                <div>
                    <PopularEvents
                        city={cityLabel}
                        cityQuery={cityParam || undefined}
                        cityIdQuery={cityIdParam || undefined}
                        dateQuery={dateParam || undefined}
                        searchQuery={searchParam || undefined}
                        minPriceQuery={minPriceParam || undefined}
                        maxPriceQuery={maxPriceParam || undefined}
                        category={activeCategory}
                        tag={tagParam || undefined}
                        initialData={initialData?.events}
                    />
                </div>

                {/* Popular Hosts - hidden for now
                <div>
                    <PopularHosts />
                </div> */}

                {/* What else is happening around town — curated external directory */}
                <div>
                    <WhatElseSection city={cityParam || undefined} />
                </div>

                {/* Host CTA */}
                <div>
                    <HostCTA />
                </div>

                {/* Footer */}
                <div>
                    <FooterSection />
                </div>
                </main>
            </div>
        </div>
    );
}
